import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { RefreshCw, Eye } from 'lucide-react';
import {
  formatDate,
  formatCurrency,
  formatReferenceType,
} from '../../utils/formatters';
import { useLedgerEntries } from '../../hooks/react-query/useGeneralLedger';
import { useChartOfAccountsGrouped } from '../../hooks/react-query/useChartOfAccounts';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { LedgerFilters } from './LedgerFilters';
import { getStatusBadgeStyle, getStatusLabel } from './transaction-labels';
import { DEFAULT_LEDGER_VIEW } from '../../types/drawdown';
import type {
  GeneralLedger,
  LedgerFilters as LedgerFiltersType,
} from '../../types/drawdown';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const journalEntryKey = (entry: GeneralLedger) =>
  entry.journal_entry_number ?? entry.transaction_id;

interface JournalEntriesTabProps {
  fundId?: string;
  onSelectEntry: (entry: GeneralLedger) => void;
}

export function JournalEntriesTab({
  fundId,
  onSelectEntry,
}: JournalEntriesTabProps) {
  const [filters, setFilters] = useState<LedgerFiltersType>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedReference = useDebouncedValue(
    filters.reference?.trim(),
    SEARCH_DEBOUNCE_MS
  );
  const debouncedCounterparty = useDebouncedValue(
    filters.counterparty?.trim(),
    SEARCH_DEBOUNCE_MS
  );
  const [searchParams] = useSearchParams();
  const referenceId = searchParams.get('reference_id') ?? undefined;

  // Prepare query parameters
  const ledgerParams = {
    fund_id: fundId || filters.fund_id,
    account_code: filters.account_code,
    reference_type: filters.reference_type,
    reference_id: referenceId,
    date_from: filters.date_from,
    date_to: filters.date_to,
    investor_id: filters.investor_id,
    reference: debouncedReference || undefined,
    counterparty: debouncedCounterparty || undefined,
    view: filters.view ?? DEFAULT_LEDGER_VIEW,
    page: currentPage,
    page_size: pageSize,
  };

  // React Query hooks
  const ledgerEntriesQuery = useLedgerEntries(ledgerParams);
  const chartOfAccountsQuery = useChartOfAccountsGrouped();

  const handleFilterChange = (
    key: keyof LedgerFiltersType,
    value: string | undefined
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const ledgerEntries = ledgerEntriesQuery.data?.entries || [];
  const totalCount = ledgerEntriesQuery.data?.total_count || 0;
  const isLoading = ledgerEntriesQuery.isLoading;

  // Transform grouped chart of accounts to flat format for filters
  const chartOfAccountsForFilters = useMemo(() => {
    if (!chartOfAccountsQuery.data) return null;
    const { assets, liabilities, equity, income, expenses } =
      chartOfAccountsQuery.data;
    const allAccounts = [
      ...assets,
      ...liabilities,
      ...equity,
      ...income,
      ...expenses,
    ];
    const chartOfAccounts: Record<string, string> = {};
    allAccounts.forEach((account) => {
      chartOfAccounts[account.account_code] = account.account_name;
    });
    return {
      chart_of_accounts: chartOfAccounts,
      categories: {
        Assets: assets.map((a) => a.account_code),
        Liabilities: liabilities.map((a) => a.account_code),
        Equity: equity.map((a) => a.account_code),
        Income: income.map((a) => a.account_code),
        Expenses: expenses.map((a) => a.account_code),
      },
    };
  }, [chartOfAccountsQuery.data]);

  const paginationPages = useMemo(() => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages
      );
    }
    return pages;
  }, [totalCount, pageSize, currentPage]);

  return (
    <Card>
      <CardHeader>
        <LedgerFilters
          filters={filters}
          fundId={fundId}
          isLoadingChartOfAccounts={chartOfAccountsQuery.isLoading}
          chartOfAccounts={chartOfAccountsForFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={() => setFilters({})}
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading entries...
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">DATE</TableHead>
                  <TableHead className="w-[120px]">REFERENCE</TableHead>
                  <TableHead className="w-[180px]">ACCOUNT</TableHead>
                  <TableHead>DESCRIPTION</TableHead>
                  <TableHead className="w-[180px]">COUNTERPARTY</TableHead>
                  <TableHead className="text-right w-[140px]">DEBIT</TableHead>
                  <TableHead className="text-right w-[140px]">CREDIT</TableHead>
                  <TableHead className="w-[110px]">STATUS</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerEntries.map((entry, index) => {
                  const prevEntry = ledgerEntries[index - 1];
                  const nextEntry = ledgerEntries[index + 1];

                  const isFirstInJournalEntry =
                    !prevEntry ||
                    journalEntryKey(prevEntry) !== journalEntryKey(entry);
                  const isLastInJournalEntry =
                    !nextEntry ||
                    journalEntryKey(nextEntry) !== journalEntryKey(entry);

                  // One journal entry can hold several investors' lines, each with its own description and reference
                  const isFirstOfDescriptionGroup =
                    isFirstInJournalEntry ||
                    prevEntry.reference_type !== entry.reference_type ||
                    prevEntry.reference !== entry.reference ||
                    prevEntry.description !== entry.description;

                  return (
                    <TableRow
                      key={entry.id}
                      className={
                        !isLastInJournalEntry ? 'border-b-0' : undefined
                      }
                    >
                      <TableCell className="align-top">
                        {isFirstOfDescriptionGroup && (
                          <span className="text-foreground">
                            {formatDate(entry.document_date)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        {isFirstOfDescriptionGroup && (
                          <span className="font-mono text-sm">
                            {entry.reference || '-'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-semibold text-foreground">
                            {entry.account_code}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {entry.account_name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        {isFirstOfDescriptionGroup && (
                          <div>
                            <div className="font-medium text-foreground">
                              {entry.description}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatReferenceType(entry.reference_type)}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.counterparty_name || '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {entry.debit_amount > 0
                          ? formatCurrency(entry.debit_amount, {
                              currency: entry.currency,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {entry.credit_amount > 0
                          ? formatCurrency(entry.credit_amount, {
                              currency: entry.currency,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="align-top">
                        {isFirstInJournalEntry && (
                          <div className="flex h-9 items-center">
                            <Badge
                              variant="outline"
                              className={getStatusBadgeStyle(entry.state)}
                            >
                              {getStatusLabel(entry.state)}
                            </Badge>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right align-top">
                        {isFirstInJournalEntry && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSelectEntry(entry)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalCount > pageSize && (
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}-
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {paginationPages.map((page, idx) =>
                page === '...' ? (
                  <span
                    key={
                      paginationPages[idx - 1] === 1
                        ? 'ellipsis-start'
                        : 'ellipsis-end'
                    }
                    className="px-2 text-muted-foreground"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    className="min-w-[36px]"
                    onClick={() => setCurrentPage(page as number)}
                  >
                    {page}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage * pageSize >= totalCount}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                disabled={currentPage * pageSize >= totalCount}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
